'use client';
import { ConfirmationDetails } from '@/components/confirmationDetails';
import { InstallmentCard, InstallmentSummary, installmentNotes } from '@/components/installmentSummary';
import { sendOnbEvent } from '@/components/newRelic';
import { BankAccountCard, LoanSummaryCard, PaymentMethodCard, RatesCard } from '@/components/reviewCards';
import { useStepData } from '@/components/stepData';
import { RootState } from '@/redux/store';
import { Box, Stack, Typography } from '@mui/material';
import { installmentRates, repaymentTerms, reviewAndSendLink } from '@repo/onb-api';
import { COLORS, OrderCard } from '@repo/ui';
import { setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function ReviewAndSendLink({
  stepname: stepName,
  stepdata: stepDataProp,
}: {
  stepname: reviewAndSendLink['name'];
  stepdata?: reviewAndSendLink['variables'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const router = useRouter();
  const errorState = useSelector((state: RootState) => state.errorState);
  // Editing only makes sense when there is another plan to pick
  const planCount = useSelector(
    (state: RootState) =>
      (state.flowdata.steps?.find(({ name }) => name === 'REPAYMENT_TERMS') as repaymentTerms | undefined)?.variables
        ?.installments?.length || 0,
  );
  const isFundsInsufficient = errorState?.error === 'INSUFFICIENT_FUNDS';
  // const variables = useSelector((state: RootState) => state.flowdata.variables);
  const {
    requestedAmount = 0,
    installmentCount = 0,
    installmentRates: { TEA: tea = 0, CFT: cft = 0, TNA: tna = 0 } = {},
    installmentAmount = 0,
    installmentFirstDueDate = '2022-01-01',
    paymentMethod,
    bankAccountReference,
    cardNumber = '1234********1234',
    cardIssuer,
    withInterest,
    installmentPlanType,
    termsAndConditions,
    paymentSchedule,
    documentNumber,
    gender,
    fullName,
    birthDate,
    taxpayerId,
    email,
    mobilePhoneNumber,
    province,
  } = stepData || {};

  const isCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';
  const additionalData = useSelector((state: RootState) => state.additionalData);
  const { merchantName = '', price = '', merchantOrderId = '', policyProduct } = additionalData;
  const subtitle = isCuotas
    ? policyProduct === 'EFECTIVOC'
      ? 'Podés modificar tus datos antes de confirmar la operación'
      : 'Podés modificar tus datos antes de confirmar la operación y disfrutar tu compra.'
    : 'Podés modificar tus datos antes de confirmar tu préstamo y empezar a disfrutarlo.';

  const installmentsOverride = {
    ...stepData?.installmentRates,
    CFTTEA: stepData?.installmentRates?.CFT,
  } as installmentRates;

  useEffect(() => {
    if (isFundsInsufficient) {
      sendOnbEvent({
        type: 'error',
        subtype: 'insufficientFunds',
        severity: 'medium',
      });
    }
  }, [isFundsInsufficient]);

  useEffect(() => {
    setClientCookie('requestedAmount', `${requestedAmount}`);
  }, [requestedAmount]);

  return (
    <StepWrapper>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography variant="h1">Revisá tu información</Typography>
        <Typography component="p" variant="subtitle2">
          {subtitle}
        </Typography>
      </Stack>
      {isCuotas ? (
        <>
          <OrderCard
            merchantName={merchantName || ''}
            merchantOrderId={merchantOrderId || ''}
            price={price || ''}
            isLoan={policyProduct === 'EFECTIVOC'}
          />
          <InstallmentCard>
            <InstallmentSummary
              label={policyProduct === 'EFECTIVOC' ? 'Lo pagás en' : 'Tu compra en cuotas'}
              count={installmentCount}
              amount={installmentAmount}
              withInterest={withInterest}
              onEdit={planCount > 1 ? () => router.push('/flow/REPAYMENT_TERMS') : undefined}
              notes={installmentNotes(installmentPlanType, installmentFirstDueDate)}
            />
          </InstallmentCard>
        </>
      ) : (
        <LoanSummaryCard
          requestedAmount={requestedAmount}
          installmentCount={installmentCount}
          installmentAmount={installmentAmount}
          firstDueDate={installmentFirstDueDate}
          onEdit={() => router.push('/flow/REPAYMENT_TERMS')}
        />
      )}
      {bankAccountReference ? (
        <Box sx={{ mt: isCuotas ? 2 : 0 }}>
          <BankAccountCard cbu={bankAccountReference} onEdit={() => router.push('/flow/BANK_ACCOUNT_REFERENCE')} />
        </Box>
      ) : (
        <></>
      )}
      <Box sx={{ mt: isCuotas && !bankAccountReference ? 2 : 0 }}>
        <PaymentMethodCard
          paymentMethod={paymentMethod}
          cardIssuer={cardIssuer}
          cardNumber={cardNumber}
          error={isFundsInsufficient}
          onEdit={() => router.push('/flow/PAYMENT_METHOD')}
        />
      </Box>
      {isFundsInsufficient ? (
        <Typography role="alert" variant="body2" color={COLORS.state.error}>
          Algo no funcionó con la tarjeta seleccionada. Tocá el lápiz y elegí otra para continuar.
        </Typography>
      ) : (
        <></>
      )}
      <RatesCard tea={tea} tna={tna} cft={cft} />
      <>
        {tna === 0 && (
          <Typography variant="body3">
            Préstamo con TNA = 0%: no se aplicará interés compensatorio. Sin embargo, si se registrara mora, el importe
            vencido e impago devengará un interés moratorio equivalente a una TNA=80 %, más un interés punitorio
            equivalente al cincuenta por ciento (50%) de dicha tasa moratoria.
          </Typography>
        )}
      </>
      {/* <OnbForm<reviewAndSendLink> stepName={stepName!} submitBlacklist={['termsAndConditions']} /> */}
      <ConfirmationDetails
        stepName={stepName}
        termsAndConditions={termsAndConditions}
        requestedAmount={requestedAmount}
        installmentCount={installmentCount}
        paymentSchedule={paymentSchedule}
        documentNumber={documentNumber}
        gender={gender}
        fullName={fullName}
        birthDate={birthDate}
        taxpayerId={taxpayerId}
        email={email}
        mobilePhoneNumber={mobilePhoneNumber}
        province={province}
        paymentMethod={paymentMethod}
        bankAccountReference={bankAccountReference}
        installmentRates={installmentsOverride}
        submitLabel="Continuar"
        readOnly
      />
    </StepWrapper>
  );
}
